/**
 * The 25 Cambodian provinces (24 + the capital). Shipping is priced per
 * province from a merchant-defined table (CLAUDE.md §3 `delivery`), so this
 * list is the closed enum both the storefront picker and the rate table use.
 *
 * Codes follow ISO 3166-2:KH so they stay stable if we ever integrate a
 * carrier API (v2: VET / J&T / GrabExpress).
 */

export interface Province {
  readonly code: string;
  readonly en: string;
  readonly km: string;
}

export const PROVINCES: readonly Province[] = [
  { code: 'KH-12', en: 'Phnom Penh', km: 'ភ្នំពេញ' },
  { code: 'KH-2', en: 'Battambang', km: 'បាត់ដំបង' },
  { code: 'KH-1', en: 'Banteay Meanchey', km: 'បន្ទាយមានជ័យ' },
  { code: 'KH-3', en: 'Kampong Cham', km: 'កំពង់ចាម' },
  { code: 'KH-4', en: 'Kampong Chhnang', km: 'កំពង់ឆ្នាំង' },
  { code: 'KH-5', en: 'Kampong Speu', km: 'កំពង់ស្ពឺ' },
  { code: 'KH-6', en: 'Kampong Thom', km: 'កំពង់ធំ' },
  { code: 'KH-7', en: 'Kampot', km: 'កំពត' },
  { code: 'KH-8', en: 'Kandal', km: 'កណ្ដាល' },
  { code: 'KH-9', en: 'Koh Kong', km: 'កោះកុង' },
  { code: 'KH-10', en: 'Kratie', km: 'ក្រចេះ' },
  { code: 'KH-11', en: 'Mondulkiri', km: 'មណ្ឌលគិរី' },
  { code: 'KH-22', en: 'Oddar Meanchey', km: 'ឧត្ដរមានជ័យ' },
  { code: 'KH-24', en: 'Pailin', km: 'បៃលិន' },
  { code: 'KH-13', en: 'Preah Vihear', km: 'ព្រះវិហារ' },
  { code: 'KH-18', en: 'Preah Sihanouk', km: 'ព្រះសីហនុ' },
  { code: 'KH-14', en: 'Prey Veng', km: 'ព្រៃវែង' },
  { code: 'KH-15', en: 'Pursat', km: 'ពោធិ៍សាត់' },
  { code: 'KH-16', en: 'Ratanakiri', km: 'រតនគិរី' },
  { code: 'KH-17', en: 'Siem Reap', km: 'សៀមរាប' },
  { code: 'KH-19', en: 'Stung Treng', km: 'ស្ទឹងត្រែង' },
  { code: 'KH-20', en: 'Svay Rieng', km: 'ស្វាយរៀង' },
  { code: 'KH-21', en: 'Takeo', km: 'តាកែវ' },
  { code: 'KH-23', en: 'Tbong Khmum', km: 'ត្បូងឃ្មុំ' },
  { code: 'KH-25', en: 'Kep', km: 'កែប' },
];

export const PROVINCE_CODES: readonly string[] = PROVINCES.map((p) => p.code);

const BY_CODE = new Map(PROVINCES.map((p) => [p.code, p]));

export function getProvince(code: string): Province | undefined {
  return BY_CODE.get(code);
}

export function isProvinceCode(code: string): boolean {
  return BY_CODE.has(code);
}

export function provinceName(code: string, locale: 'en' | 'km'): string {
  const province = BY_CODE.get(code);
  if (!province) return code;
  return locale === 'km' ? province.km : province.en;
}
